import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputCheckboxField } from "../../../../components/Shared/InputCheckboxField";
import { InputTextAreaField } from "../../../../components/Shared/InputTextAreaField";
import { request } from "../../../../utils/Request";
import { Divider } from "primereact/divider";
import InputDateTime from "../../../../components/Shared/InputDateTime";

interface IProps {
    visible: any;
    toastify: any;
    caseNumber?: string;
    onClose: any;
}

type TInputForm = {
    is_repaired: boolean;
    not_repair_required: boolean;
    etr: null | string;
    comments: null | string;
    close_label: boolean;
    close_all_labels: boolean;
};

const DEFAULT_FORM_STATE = {
    is_repaired: false,
    not_repair_required: false,
    etr: null,
    comments: null,
    close_label: false,
    close_all_labels: false,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        etr: yup.date().nullable().required(t('the field is required.', { field: t('etr') })),
        not_repair_required: yup.boolean().when('is_repaired', {
            is: false,
            then: (schema: any) => schema.oneOf([true], t('the field is required.', { field: t('not repaired at compound') })),
            otherwise: (schema: any) => schema.nullable(),
        }),
        is_repaired: yup.boolean().when('not_repair_required', {
            is: false,
            then: (schema: any) => schema.oneOf([true], t('the field is required.', { field: t('is repaired?') })),
            otherwise: (schema: any) => schema.nullable(),
        }),
    }, [['is_repaired', 'not_repair_required']]);
};

function ClosedOrderDamageCaseUpdateETR(props: IProps) {
    const { toastify } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [open, setOpen] = useState<boolean>(false);
    const { t }: any = useTranslation();

    const onClose = () => {
        setOpen(false);
        props.onClose();
    };

    const onSubmit = async (payloads: TInputForm) => {
        try {
            await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${props.visible}/update/etr`,
                data: {
                    id: props.visible,
                    etr: payloads.etr,
                    not_repair_required: payloads.not_repair_required || false,
                    is_repaired: payloads.is_repaired || false,
                    comments: payloads.comments,
                    close_label: payloads.close_label || false,
                    close_all_labels: payloads.close_all_labels || false,
                },
            });

            toastify(t("record updated", { item: t('damage case') }), "info");
            onClose();
        } catch (e: any) {
            if (e.status === 422 && e.data && e.data.errors) {
                toastify(t("validation error"), "error");
            } else if (e.status === 404) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if (base) {
            base.resetForm();
        }
        setInitFormState(DEFAULT_FORM_STATE);
    };

    useEffect(() => {
        if (props.visible) {
            setOpen(true);
            setInitFormState(DEFAULT_FORM_STATE);
        } else {
            setOpen(false);
        }
    }, [props.visible]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className="card-title mt-0">
                                {props.caseNumber ? t('update etr') + ' #' + props.caseNumber : t('update etr')}
                            </h5>
                            <p>{t("update estimated time of repair information.")}</p>
                        </div>
                    }
                    visible={open}
                    position="left"
                    onHide={onClose}
                    blockScroll={true}
                    style={{ width: '800px' }}
                >
                    <div style={{
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    }}></div>

                    <Formik
                        innerRef={formRef}
                        enableReinitialize={true}
                        initialValues={initFormState}
                        onSubmit={onSubmit}
                        validationSchema={validationSchema(t)}
                    >
                        {(formProps: FormikProps<TInputForm>) => {
                            const isRepairedValue = formProps.values.is_repaired;

                            return (
                                <Form onSubmit={formProps.handleSubmit} className="mb-5">
                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={12} md={6}>
                                            <Field
                                                component={InputCheckboxField}
                                                name="not_repair_required"
                                                label={t("not repaired at compound")}
                                            />
                                        </Col>
                                        <Col sm={12} md={6}>
                                            <Field
                                                component={InputCheckboxField}
                                                name="is_repaired"
                                                label={t("is repaired?")}
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={12} md={6}>
                                            <Field
                                                component={InputDateTime}
                                                type={"date"}
                                                name="etr"
                                                label={t("etr")}
                                                placeholder={t("select") + ' ' + t("date").toLowerCase()}
                                                isRequired={ true }
                                            />
                                        </Col>
                                    </Row>

                                    <Divider />

                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={12}>
                                            <Field
                                                component={InputTextAreaField}
                                                name="comments"
                                                label={t("comments")}
                                                placeholder={t("enter") + ' ' + t("comments").toLowerCase()}
                                                rows={4}
                                            />
                                        </Col>
                                    </Row>

                                    <Divider />

                                    <Row className="mt-4 md:mb-4 sm:mb-4">
                                        <Col sm={12} md={6}>
                                            <Field
                                                component={InputCheckboxField}
                                                name="close_label"
                                                label={t("mark as done (remove label)")}
                                            />
                                        </Col>
                                        <Col sm={12} md={6}>
                                            <Field
                                                component={InputCheckboxField}
                                                name="close_all_labels"
                                                label={t("remove all labels")}
                                            />
                                        </Col>
                                    </Row>

                                    <Divider />

                                    <Row>
                                        <Col sm={12}>
                                            <div className="filter-card-footer" style={{ width: '785px' }}>
                                                <Button
                                                    type="submit"
                                                    disabled={formProps.isSubmitting}
                                                    tooltip={t("to data save into database")}
                                                    className="ms-2"
                                                    size="small"
                                                    outlined
                                                    tooltipOptions={{ position: 'top' }}
                                                >
                                                    <i className="pi pi-save me-2" />
                                                    {t("save")}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    disabled={formProps.isSubmitting}
                                                    onClick={onReset}
                                                    tooltip={t("to make all fields empty")}
                                                    className="ms-2"
                                                    size="small"
                                                    severity="warning"
                                                    outlined
                                                    tooltipOptions={{ position: 'top' }}
                                                >
                                                    <i className="pi pi-refresh me-2" />
                                                    {t("reset")}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    tooltip={t("operation cancel")}
                                                    className="ms-2"
                                                    size="small"
                                                    severity="danger"
                                                    outlined
                                                    tooltipOptions={{ position: 'top' }}
                                                    onClick={onClose}
                                                >
                                                    <i className="pi pi-arrow-left me-2" />
                                                    {t("cancel")}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            );
                        }}
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

export default withTranslation()(ClosedOrderDamageCaseUpdateETR);

